import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Icon from './Icon';
import useUnreadMessages from '../hooks/useUnreadMessages';
import { apiFetch } from '../config/api';
import '../styles/Navbar.css';

const ROLE_LABEL = { client:'Klient', salon:'Salon', admin:'Administrator', franchisee:'Franczyzobiorca' };
const ROLE_DASH  = { client:'/dashboard/client', salon:'/dashboard/salon', admin:'/dashboard/admin', franchisee:'/dashboard/franchisee' };

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { unreadCount } = useUnreadMessages(user);
  const [notifUnread, setNotifUnread] = React.useState(0);

  React.useEffect(() => {
    if (!user?.id) return;
    const check = async () => {
      try {
        const data = await apiFetch('/notifications');
        setNotifUnread((data || []).filter(n => !n.is_read).length);
      } catch { }
    };
    check();
    const id = setInterval(check, 10000);
    window.addEventListener('hairkus-notif-update', check);
    return () => { clearInterval(id); window.removeEventListener('hairkus-notif-update', check); };
  }, [user?.id]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => { 
    setDropdownOpen(false); 
    const overlay = document.createElement('div');
    overlay.style.cssText = `position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); display: flex; flex-direction: column; align-items: center; justify-content: center; z-index: 10000; color: white; font-family: 'Poppins', sans-serif;`;
    const style = document.createElement('style');
    style.textContent = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`;
    document.head.appendChild(style);
    overlay.innerHTML = `<div style="text-align: center;"><div style="width: 60px; height: 60px; border: 4px solid rgba(255,255,255,0.3); border-top-color: white; border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 30px;"></div><h2 style="font-size: 2em; margin: 0 0 10px 0;">Wylogowywanie...</h2><p style="opacity: 0.9;">Do zobaczenia wkrótce! </p></div>`;
    document.body.appendChild(overlay);
    setTimeout(async () => {
      try { await logout(); } catch (err) { localStorage.clear(); window.location.href = '/login'; }
      finally { if (document.body.contains(overlay)) document.body.removeChild(overlay); }
    }, 1200);
  };

  const getInitials = () => {
    if (!user) return '?';
    return ((user.firstName?.[0] || '') + (user.lastName?.[0] || '')).toUpperCase();
  };

  const BrandLogo = () => (
    <div className="navbar-logo-container">
      <img src="/logo.png" alt="Logo" className="navbar-small-logo" />
      <div className="navbar-logo-text">
        <span className="logo-hair-nav">HAIR</span><span className="logo-kus-nav">KUS</span>
      </div>
    </div>
  );

  if (!isAuthenticated) {
    return (
      <nav className="navbar">
        <div className="navbar-container">
          <Link to="/" className="navbar-logo"><BrandLogo /></Link>
          <div className="navbar-auth-buttons">
            <Link to="/login" className="nav-button login-btn">Zaloguj sie</Link>
            <Link to="/register" className="nav-button register-btn">Zarejestruj sie</Link>
          </div>
        </div>
      </nav>
    );
  }

  const dashLink = ROLE_DASH[user?.role] || '/';

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to={dashLink} className="navbar-logo"><BrandLogo /></Link>
        <div className="navbar-right">
          <button className="navbar-bell" title="Wiadomości" onClick={() => navigate(dashLink, { state: { openTab: 'messages' } })}>
            <Icon name="notification" />
            {(unreadCount + notifUnread) > 0 && <span className="navbar-badge">{(unreadCount + notifUnread)}</span>}
          </button>
          <div className="navbar-user" ref={dropdownRef}>
            <div className="user-avatar-wrapper" onClick={() => setDropdownOpen(!dropdownOpen)}>
              <div className="user-avatar">{getInitials()} {unreadCount > 0 && <span className="avatar-dot" />}</div>
              <div className="user-info">
                <span className="user-name">{user.firstName} {user.lastName}</span>
                <span className="user-role">{ROLE_LABEL[user.role]}</span>
              </div>
              <Icon name="chevron-down" className={`dropdown-arrow-icon ${dropdownOpen ? 'open' : ''}`} />
            </div>
            {dropdownOpen && (
              <div className="user-dropdown">
                <Link to={dashLink} className="dropdown-item" onClick={() => setDropdownOpen(false)}>Moj panel</Link>
                <Link to="/profile" className="dropdown-item" onClick={() => setDropdownOpen(false)}>Edycja profilu</Link>
                <div className="dropdown-divider" />
                <button className="dropdown-item logout-item" onClick={handleLogout}>Wyloguj sie</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;